/**
 * Settings → Usage (slice 2a part 2 §4): the plan, today's web lookups
 * against the cap, and this month's by tool, from `GET /api/usage`. The
 * owner's.
 */
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import type { ReactNode } from "react";
import { errorHint } from "../../app/api";
import { useUsage, type Usage } from "./api";
import { monthBars, todayMeter } from "./settings-view";
import { sizeLabel } from "../files/kinds";

export function UsageTab() {
  const usage = useUsage();
  if (usage.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  // A read that failed shows that, never the last read's counts (Devin on #212).
  if (usage.isError || !usage.data) {
    return <p className="text-body text-destructive">{errorHint(usage.error)}</p>;
  }
  const u = usage.data;
  return (
    <div className="flex flex-col gap-5">
      <Plan plan={u.plan} />
      <Today web={u.web} />
      <ByTool month={u.month} />
      {u.storage ? <Storage storage={u.storage} /> : null}
      <p className="text-sm text-muted-foreground">
        Your AI providers bill you for the thinking, not Crew. See their usage in{" "}
        <Link href="/settings#ai" className="underline">
          AI
        </Link>
        .
      </p>
    </div>
  );
}

function Card({
  label,
  aside,
  children,
}: {
  label?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={label}
      className="flex flex-col gap-3 rounded-xl bg-card px-4 py-4 shadow-[0_0_0_1px_rgb(31_42_68/0.08)] md:px-5"
    >
      {label ? (
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
            {label}
          </h2>
          {aside ? <span className="text-[13px] text-muted-foreground">{aside}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** The plan: no renewal date or Manage billing until the billing stage (§4.1). */
function Plan({ plan }: { plan: Usage["plan"] }) {
  return (
    <Card>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-foreground">{plan.name}</span>
          {plan.status === "active" ? (
            <span className="rounded bg-moss/10 px-1.5 py-0.5 text-xs font-medium text-moss">
              Active
            </span>
          ) : null}
        </div>
        <span className="text-sm text-muted-foreground">${plan.price_usd} a month</span>
      </div>
    </Card>
  );
}

/**
 * Today's lookups against the cap (§4.2): "Back to 200 at midnight" top
 * right on a desktop, under the bar on a phone (Paper 32, 32b); copper, with
 * the thread's capped words, at the cap.
 */
function Today({ web }: { web: Usage["web"] }) {
  const m = todayMeter(web);
  const back = `Back to ${web.limit} at midnight`;
  return (
    <Card label="Web lookups today" aside={<span className="hidden md:inline">{back}</span>}>
      <Bar percent={m.percent} className={m.capped ? "bg-copper" : "bg-moss"} />
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
        <span className="text-copy">{m.used}</span>
        {m.capped ? (
          <span className="text-copper">Web lookups used up for today · back at midnight</span>
        ) : (
          <>
            <span className="hidden font-medium text-foreground md:inline">{m.left}</span>
            <span className="text-muted-foreground md:hidden">{back}</span>
          </>
        )}
      </div>
    </Card>
  );
}

function ByTool({ month }: { month: Usage["month"] }) {
  const bars = monthBars(month);
  return (
    <Card label="Usage by tool" aside="This month">
      {bars ? (
        <ul className="flex flex-col gap-3">
          {bars.map((b) => (
            <li key={b.id} data-tool={b.id} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between text-[15px] text-foreground">
                <span>{b.name}</span>
                <span className="tabular-nums">{b.count}</span>
              </div>
              <Bar percent={b.percent} className="bg-foreground" />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No lookups yet this month.</p>
      )}
    </Card>
  );
}

/** The files the workspace keeps (slice 5b spec §10), against its 10 GB; copper near the top. */
function Storage({ storage }: { storage: NonNullable<Usage["storage"]> }) {
  const percent = storage.limit > 0 ? Math.min(100, (storage.used / storage.limit) * 100) : 0;
  const near = percent >= 90;
  return (
    <Card label="Files kept" aside={`${sizeLabel(storage.limit)} included`}>
      <Bar
        percent={Math.max(percent, storage.used > 0 ? 1 : 0)}
        className={near ? "bg-copper" : "bg-moss"}
      />
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
        <span className="text-copy" data-storage-used>
          {sizeLabel(storage.used)} of {sizeLabel(storage.limit)}
        </span>
        <span className={near ? "text-copper" : "text-muted-foreground"}>
          {near
            ? "Nearly full: delete docs no one needs to make room."
            : "Photos, files and docs people and the crew kept."}
        </span>
      </div>
    </Card>
  );
}

function Bar({ percent, className }: { percent: number; className: string }) {
  return (
    <div
      role="meter"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-1 w-full overflow-hidden rounded-full bg-foreground/[0.06]"
    >
      <div className={cn("h-full rounded-full", className)} style={{ width: `${percent}%` }} />
    </div>
  );
}
